import { useState, useEffect, useCallback } from 'react';
import { socialAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  MessageSquare, Sparkles, Calendar, Image, Check, X, RefreshCw, 
  Download, Trash2, Clock, Loader2, Facebook, Building2
} from 'lucide-react';

// Helper to log only in development
const devLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

// Status color mapping
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  posted: 'bg-blue-100 text-blue-700',
  generating: 'bg-purple-100 text-purple-700',
  regenerating_image: 'bg-purple-100 text-purple-700',
  failed: 'bg-red-100 text-red-700',
  default: 'bg-gray-100 text-gray-700'
};

const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.default;

const CONTENT_TYPES = [
  { value: 'before_after', label: 'Before & After', description: 'Showcase transformations' },
  { value: 'tips', label: 'Tips & Education', description: 'Helpful homeowner advice' },
  { value: 'promotion', label: 'Promotion', description: 'Special offers & deals' },
  { value: 'general', label: 'General', description: 'Custom content' }
];

const SocialPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Form state
  const [contentType, setContentType] = useState('general');
  const [platform, setPlatform] = useState('both');
  const [userPrompt, setUserPrompt] = useState('');

  // Polling for generation status
  const [pollingPostId, setPollingPostId] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = activeTab === 'all' ? null : activeTab;
      const data = await socialAPI.getAll(statusFilter);
      setPosts(data);
    } catch (error) {
      devLog('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Poll for generation completion
  useEffect(() => {
    if (!pollingPostId) return;
    
    const interval = setInterval(async () => {
      try {
        const post = await socialAPI.getOne(pollingPostId);
        if (post.status !== 'generating' && post.status !== 'regenerating_image') {
          setPollingPostId(null);
          fetchPosts();
        }
      } catch (error) {
        devLog('Polling error:', error);
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [pollingPostId, fetchPosts]);

  const generatePost = async () => {
    if (!userPrompt.trim()) {
      alert('Please describe what content you want to generate');
      return;
    }

    setGenerating(true);
    try {
      const result = await socialAPI.generate({
        platform: platform,
        content_type: contentType,
        user_prompt: userPrompt
      });
      
      setPollingPostId(result.id);
      setUserPrompt('');
      setActiveTab('pending');
      fetchPosts();
    } catch (error) {
      devLog('Error generating post:', error);
      alert('Failed to start content generation');
    } finally {
      setGenerating(false);
    }
  };

  const approvePost = async (postId) => {
    try {
      await socialAPI.approve(postId);
      fetchPosts();
    } catch (error) {
      devLog('Error approving post:', error);
      alert('Failed to approve post');
    }
  };

  const rejectPost = async (postId) => {
    try {
      await socialAPI.reject(postId);
      fetchPosts();
    } catch (error) {
      devLog('Error rejecting post:', error);
      alert('Failed to reject post');
    }
  };

  const regenerateCaption = async (postId) => {
    try {
      await socialAPI.regenerateCaption(postId);
      fetchPosts();
    } catch (error) {
      devLog('Error regenerating caption:', error);
      alert('Failed to regenerate caption');
    }
  };

  const regenerateImage = async (postId) => {
    try {
      await socialAPI.regenerateImage(postId);
      setPollingPostId(postId);
      fetchPosts();
    } catch (error) {
      devLog('Error regenerating image:', error);
      alert('Failed to regenerate image');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await socialAPI.delete(postId);
      fetchPosts();
    } catch (error) {
      devLog('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const downloadImage = (base64Data, postId) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = `social-post-${postId}.png`;
    link.click();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Caption copied to clipboard!');
  };

  return (
    <div className="p-8" data-testid="social-posts-page">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">AI Social Media Poster</h1>
        <p className="font-manrope text-gray-600">Generate AI-powered images and captions for your social media</p>
      </div>

      {/* Generator Card */}
      <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-8" data-testid="generator-card">
        <CardHeader>
          <CardTitle className="font-barlow text-2xl uppercase flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Generate New Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Content Type Selection */}
            <div>
              <label className="font-manrope font-semibold block mb-3">Content Type</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {CONTENT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setContentType(type.value)}
                    className={`p-3 border-2 rounded-sm text-left transition-all ${
                      contentType === type.value 
                        ? 'border-primary bg-primary/10' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    data-testid={`content-type-${type.value}`}
                  >
                    <p className="font-manrope font-semibold text-sm">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Platform Selection */}
            <div>
              <label className="font-manrope font-semibold block mb-3">Target Platform</label>
              <div className="flex gap-3">
                {[
                  { value: 'both', label: 'Both Platforms', icon: null },
                  { value: 'facebook', label: 'Facebook', icon: Facebook },
                  { value: 'google_business', label: 'Google Business', icon: Building2 }
                ].map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPlatform(p.value)}
                    className={`px-4 py-2 border-2 rounded-sm flex items-center gap-2 transition-all ${
                      platform === p.value 
                        ? 'border-primary bg-primary/10' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {p.icon && <p.icon className="h-4 w-4" />}
                    <span className="font-manrope text-sm">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt Input */}
            <div>
              <label className="font-manrope font-semibold block mb-2">
                Describe what you want to post about
              </label>
              <Textarea
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                className="border-2 border-black min-h-[100px] font-manrope"
                placeholder={
                  contentType === 'before_after' 
                    ? "E.g., Just finished a tile roof restoration in Winter Park. The homeowner was thrilled with the cooler temperatures inside!"
                    : contentType === 'tips'
                    ? "E.g., Tips for preparing your roof for hurricane season"
                    : contentType === 'promotion'
                    ? "E.g., Spring special - $200 off any roof coating over 1500 sq ft"
                    : "Describe the content you want to create..."
                }
                data-testid="prompt-input"
              />
            </div>

            <Button
              onClick={generatePost}
              disabled={generating || !userPrompt.trim()}
              className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] w-full md:w-auto"
              data-testid="generate-button"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Content
                </>
              )}
            </Button>

            <p className="text-sm text-gray-500 font-manrope">
              AI will generate both an image and caption. You can review and edit before posting.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {['pending', 'approved', 'generating', 'rejected', 'all'].map((tab) => (
          <Button
            key={tab}
            onClick={() => setActiveTab(tab)}
            variant={activeTab === tab ? 'default' : 'outline'}
            className={`capitalize ${
              activeTab === tab 
                ? 'bg-secondary text-white border-2 border-black' 
                : 'border-2 border-black'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'generating' ? 'In Progress' : tab}
          </Button>
        ))}
      </div>

      {/* Posts List */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="font-manrope text-gray-600 mt-2">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <Card className="border-2 border-gray-200">
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="font-manrope text-gray-600">No {activeTab} posts yet</p>
              <p className="text-sm text-gray-400 mt-1">Generate your first AI content above!</p>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card 
              key={post.id} 
              className="border-2 border-black shadow-[4px_4px_0px_0px_#000]"
              data-testid={`post-card-${post.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Image Section */}
                  <div className="lg:w-1/3">
                    {post.status === 'generating' || post.status === 'regenerating_image' ? (
                      <div className="w-full aspect-square bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <div className="text-center">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-2" />
                          <p className="text-sm text-gray-500">
                            {post.status === 'regenerating_image' ? 'Regenerating image...' : 'Generating content...'}
                          </p>
                        </div>
                      </div>
                    ) : post.image_base64 ? (
                      <div className="relative group">
                        <img 
                          src={`data:image/png;base64,${post.image_base64}`}
                          alt="Generated content"
                          className="w-full aspect-square object-cover border-2 border-black"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => downloadImage(post.image_base64, post.id)}
                            className="bg-white text-black hover:bg-gray-100"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => regenerateImage(post.id)}
                            className="bg-white text-black hover:bg-gray-100"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <div className="text-center">
                          <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">No image</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="lg:w-2/3 space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(post.status)}`}>
                          {post.status === 'regenerating_image' ? 'Regenerating' : post.status}
                        </span>
                        <span className="text-xs px-3 py-1 bg-gray-100 rounded-full font-semibold capitalize">
                          {post.content_type || 'general'}
                        </span>
                        {post.platform && (
                          <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold capitalize">
                            {post.platform === 'both' ? 'All Platforms' : post.platform.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deletePost(post.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Caption */}
                    <div className="bg-gray-50 p-4 rounded-sm border">
                      <p className="font-manrope text-sm whitespace-pre-wrap">{post.caption}</p>
                    </div>

                    {/* Hashtags */}
                    {post.hashtags && post.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {post.hashtags.map((tag) => (
                          <span key={tag} className="text-xs text-primary font-semibold">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Original Prompt */}
                    {post.user_prompt && (
                      <div className="text-xs text-gray-500 italic">
                        Original request: &ldquo;{post.user_prompt}&rdquo;
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Created: {new Date(post.created_at).toLocaleString()}
                      </span>
                    </div>

                    {/* Actions */}
                    {post.status === 'pending' && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        <Button
                          onClick={() => approvePost(post.id)}
                          className="bg-green-600 hover:bg-green-700 text-white border-2 border-black"
                          data-testid={`approve-${post.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => rejectPost(post.id)}
                          variant="outline"
                          className="border-2 border-black text-red-600 hover:bg-red-50"
                          data-testid={`reject-${post.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => regenerateCaption(post.id)}
                          variant="outline"
                          className="border-2 border-black"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          New Caption
                        </Button>
                        <Button
                          onClick={() => copyToClipboard(post.caption + '\n\n' + (post.hashtags || []).join(' '))}
                          variant="outline"
                          className="border-2 border-black"
                        >
                          Copy Text
                        </Button>
                      </div>
                    )}

                    {post.status === 'approved' && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        <Button
                          onClick={() => copyToClipboard(post.caption + '\n\n' + (post.hashtags || []).join(' '))}
                          className="bg-blue-600 hover:bg-blue-700 text-white border-2 border-black"
                        >
                          Copy Caption & Hashtags
                        </Button>
                        {post.image_base64 && (
                          <Button
                            onClick={() => downloadImage(post.image_base64, post.id)}
                            variant="outline"
                            className="border-2 border-black"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Image
                          </Button>
                        )}
                        <p className="w-full text-sm text-gray-500 mt-2">
                          Ready to post! Download the image and copy the caption to post on Facebook & Google Business Profile.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SocialPosts;
